import { Navigation } from '@/components/Navigation';
import { Footer } from '@/components/Footer';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Calendar, Clock, User, ArrowLeft, Share2 } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const PowerOfRituals = () => {
  const navigate = useNavigate();

  const handleGoBack = () => {
    navigate('/blog');
  };

  return (
    <div className="min-h-screen bg-background">
      <Navigation />
      
      {/* Hero Section */}
      <section className="pt-32 pb-16 px-6">
        <div className="max-w-4xl mx-auto">
          <Button 
            variant="ghost" 
            onClick={handleGoBack}
            className="mb-8 -ml-4 text-muted-foreground hover:text-foreground"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Blog
          </Button>
          
          <div className="mb-8">
            <Badge className="mb-4">Lifestyle</Badge>
            <h1 className="text-4xl md:text-6xl font-serif font-light mb-6 text-foreground leading-tight">
              The Power of Rituals – Why We Need Simple Ceremonies in Life
            </h1>
            
            <div className="flex items-center gap-6 text-sm text-muted-foreground mb-8">
              <div className="flex items-center gap-2">
                <Calendar className="w-4 h-4" />
                <span>July 19, 2025</span>
              </div>
              <div className="flex items-center gap-2">
                <Clock className="w-4 h-4" />
                <span>4 min read</span>
              </div>
              <div className="flex items-center gap-2">
                <User className="w-4 h-4" />
                <span>Piotr</span>
              </div>
            </div>
            
            <div className="flex items-center gap-4">
              <Button variant="outline" size="sm">
                <Share2 className="w-4 h-4 mr-2" />
                Share Article
              </Button>
            </div>
          </div>
          
          <div className="relative overflow-hidden rounded-2xl mb-12">
            <img 
              src="/lovable-uploads/6b932101-db8c-4e5e-bb2d-87f4128dae97.png" 
              alt="Open book with tea cup on wooden table in peaceful garden setting representing daily rituals"
              className="w-full h-[400px] md:h-[500px] object-cover grayscale"
            />
          </div>
        </div>
      </section>

      {/* Article Content */}
      <article className="px-6 pb-24">
        <div className="max-w-4xl mx-auto">
          <div className="prose prose-lg max-w-none">
            <div className="text-xl leading-relaxed text-muted-foreground mb-8 font-light italic">
              From morning tea to shared meals, rituals shape the rhythm of our days. Far from being trivial, rituals are anchors of meaning, providing stability and comfort in an unpredictable world.
            </div>
            
            <div className="space-y-8 text-lg leading-relaxed text-foreground">
              <h2 className="text-3xl font-serif font-light mb-12 mt-12 text-foreground">The psychology of rituals</h2>

              <p className="mb-6 leading-relaxed">
                Studies suggest that rituals help reduce anxiety, increase feelings of control, and strengthen social bonds (Norton & Gino, 2014). Rituals provide structure, signaling transitions and marking moments that matter. They also create a <strong className="text-accent">sense of belonging within families, communities, and cultures</strong>.
              </p>

              <div className="h-px bg-gradient-to-r from-transparent via-border to-transparent my-12"></div>

              <h2 className="text-3xl font-serif font-light mb-12 mt-12 text-foreground">Examples of everyday rituals</h2>

              <ul className="space-y-3 ml-6 mb-6">
                <li><strong className="text-accent">A morning walk</strong> to start the day with clarity.</li>
                <li><strong className="text-accent">Lighting a candle at dinner</strong> to bring presence.</li>
                <li><strong className="text-accent">Weekly gatherings</strong> with friends or family.</li>
                <li><strong className="text-accent">Evening journaling</strong> as a way to reflect and close the day.</li>
              </ul>

              <p className="mb-6 leading-relaxed">
                These simple practices carry profound psychological benefits, grounding us in moments of stillness.
              </p>

              <div className="h-px bg-gradient-to-r from-transparent via-border to-transparent my-12"></div>

              <h2 className="text-3xl font-serif font-light mb-12 mt-12 text-foreground">Cultural roots</h2>

              <p className="mb-6 leading-relaxed">
                Across cultures, rituals have always been central to human experience – from indigenous ceremonies to religious traditions. Their universality highlights a <strong className="text-accent">shared human need for rhythm, connection, and transcendence</strong>.
              </p>

              <div className="h-px bg-gradient-to-r from-transparent via-border to-transparent my-12"></div>

              <h2 className="text-3xl font-serif font-light mb-12 mt-12 text-foreground">Conclusion</h2>

              <p className="mb-6 leading-relaxed">
                In a world of speed and distraction, rituals remind us to pause, connect, and honor the moment. They are not old-fashioned customs but timeless practices for resilience and meaning.
              </p>

              <p className="text-xl font-light italic text-accent mb-6">
                In simple ceremonies, we find profound meaning.
              </p>

              <div className="bg-card p-8 rounded-2xl mt-16 border border-border/20">
                <h3 className="text-2xl font-serif font-light mb-4 text-foreground">References</h3>
                <div className="space-y-2 text-sm text-muted-foreground">
                  <p>• Norton, M. I., & Gino, F. (2014). Rituals alleviate grieving, enhance consumption, and boost confidence. <em>Scientific American Mind</em>.</p>
                  <p>• Hobson, N. M., Schroeder, J., Risen, J. L., Xygalatas, D., & Inzlicht, M. (2018). The psychology of rituals: An integrative review and process-based framework. <em>Personality and Social Psychology Review, 22</em>(3), 260–284.</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </article>

      <Footer />
    </div>
  );
};

export default PowerOfRituals;